import React, { useMemo } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Flex, IconButton, Image, Text } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { FullPageLoader } from '../../../../common/components/Loader/FullPageLoader';
import { DndTable } from '../../../../common/components/Table/DndTable';
import { RowDragHandleCell } from '../../../../common/components/Table/RowDragHandleCell';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import { useQuickContactsFilterContext } from '../../context/QuickContactsFilterContext/hooks/useQuickContactsFilterContext';
import { QuickContactType } from '../../model/enums/quick-contact-type.enum';
import { DeleteQuickContactModal } from '../DeleteQuickContact/DeleteQuickContactModal';
import { EditQuickContactModal } from '../EditQuickContact/EditQuickContactModal';
import { QuickContactTableRow } from './model/quick-contact-table-row';
import { useQuickContactsTable } from './utils/use-quick-contacts-table.hook';

const getIconPath = (iconName: string) => require(`../../../../assets/icons/${iconName}`);

export const QuickContactsTable = () => {
  const translations = useTranslations();
  const {
    quickContacts,
    isLoadingQuickContacts,
    selectedQuickContact,
    isDeleteModalOpen,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    isEditModalOpen,
    handleOpenEditModal,
    handleCloseEditModal,
  } = useQuickContactsTable();

  const { quickContactsState, handleSetNewOrder } = useQuickContactsFilterContext();

  const columns = useMemo<ColumnDef<QuickContactTableRow>[]>(
    () => [
      {
        id: 'drag-handle',
        cell: ({ row }) => (
          <RowDragHandleCell rowId={row.original.uniqueId} isDisabled={!quickContactsState.isReorderMode} />
        ),
      },
      {
        accessorKey: 'quickContactName',
        header: translations('quick_contacts_client_header'),
        cell: ({ row }) => (
          <Flex alignItems="center">
            <Image
              src={getIconPath(row.original.type === QuickContactType.RESTAURANT ? 'pin.svg' : 'user.svg')}
              my="auto"
              mr={2}
              alt="pin"
            />
            <Text>{row.original.quickContactName}</Text>
          </Flex>
        ),
      },
      {
        accessorKey: 'formattedAddress',
        header: translations('quick_contacts_address_header'),
        cell: ({ row }) => (
          <Flex>
            <Text>{row.original.address?.formattedAddress}</Text>
          </Flex>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <Flex justifyContent={'flex-end'}>
            {row.original.type === QuickContactType.RESTAURANT ? null : (
              <IconButton
                isDisabled={quickContactsState.isReorderMode}
                variant={'orangeSolid'}
                aria-label="edit"
                color={'black'}
                icon={<EditIcon />}
                onClick={handleOpenEditModal(row.original)}
                mr={2}
              />
            )}
            <IconButton
              isDisabled={quickContactsState.isReorderMode}
              variant={'redSolid'}
              aria-label="delete"
              color={'white'}
              icon={<DeleteIcon />}
              onClick={handleOpenDeleteModal(row.original)}
            />
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line
    [quickContactsState.isReorderMode], // FYI: cannot include translations and handleOpenDeleteModal to not spoil the reorder function, don't know why yet
  );

  return (
    <>
      <FullPageLoader show={isLoadingQuickContacts}>
        {quickContacts ? (
          <DndTable
            isReorderMode={quickContactsState.isReorderMode}
            entry={quickContacts}
            search={quickContactsState.search}
            columns={columns}
            variant={'quickContacts'}
            onNewOrder={handleSetNewOrder}
          />
        ) : null}
      </FullPageLoader>
      {selectedQuickContact ? (
        <>
          <DeleteQuickContactModal
            isOpen={isDeleteModalOpen}
            onClose={handleCloseDeleteModal}
            quickContact={selectedQuickContact}
          />
          <EditQuickContactModal
            quickContact={selectedQuickContact}
            isOpen={isEditModalOpen}
            onClose={handleCloseEditModal}
          />
        </>
      ) : null}
    </>
  );
};
